import ShopifyBuy from 'shopify-buy';

export default class CartManager {
	constructor() {
		// Bind methods first so UI always works
		this.showSidebarCart = this.showSidebarCart.bind(this);
		this.closeSidebarCart = this.closeSidebarCart.bind(this);
		this.handleOutsideClick = this.handleOutsideClick.bind(this);

		// Always attach UI listeners so cart can open/close
		this.attachCartIconListener();
		this.attachCloseCartListener();

		// Validate environment variables for Shopify functionality
		if (!process.env.SHOPIFY_HOSTNAME || !process.env.SHOPIFY_STOREFRONT_ACCESS_TOKEN) {
			return;
		}

		this.client = ShopifyBuy.buildClient({
			domain: process.env.SHOPIFY_HOSTNAME,
			storefrontAccessToken: process.env.SHOPIFY_STOREFRONT_ACCESS_TOKEN,
		});
		this.cartId = localStorage.getItem('cartId');

		this.init = this.init.bind(this);
		this.createCart = this.createCart.bind(this);
		this.fetchCart = this.fetchCart.bind(this);
		this.addToCart = this.addToCart.bind(this);
		this.updateCartDisplay = this.updateCartDisplay.bind(this);
		this.removeItemFromCart = this.removeItemFromCart.bind(this);
		this.clearCart = this.clearCart.bind(this);
		this.initVariantSelection = this.initVariantSelection.bind(this);
		this.updateVariantSelection = this.updateVariantSelection.bind(this);
		this.showToast = this.showToast.bind(this);

		this.init();
	}

	showToast(message, type = 'error') {
		const container = document.getElementById('toast-container');
		if (!container) {
			alert(message);
			return;
		}

		const toast = document.createElement('div');
		toast.className = `
			toast px-16 py-12 rounded-full text-sm text-center
			transform translate-y-4 opacity-0 transition-all duration-300 ease-out
			pointer-events-auto backdrop-blur-lg
			${type === 'error' ? 'bg-blue text-white' : type === 'success' ? 'bg-blue text-white' : 'bg-blue text-white'}
		`;
		toast.textContent = message;

		container.appendChild(toast);

		// Trigger animation
		requestAnimationFrame(() => {
			toast.classList.remove('translate-y-4', 'opacity-0');
			toast.classList.add('translate-y-0', 'opacity-100');
		});

		// Auto-remove after 4 seconds
		setTimeout(() => {
			toast.classList.remove('translate-y-0', 'opacity-100');
			toast.classList.add('translate-y-4', 'opacity-0');
			setTimeout(() => toast.remove(), 300);
		}, 4000);
	}

	async init() {
		// Check if environment variables are available
		if (!process.env.SHOPIFY_HOSTNAME || !process.env.SHOPIFY_STOREFRONT_ACCESS_TOKEN) {
			return;
		}

		try {
			if (!this.cartId) {
				await this.createCart();
			} else {
				const cart = await this.fetchCart(this.cartId);
				if (cart) {
					this.updateCartDisplay(cart);
				} else {
					// Cart ID is invalid, create a new one
					localStorage.removeItem('cartId');
					await this.createCart();
				}
			}

			this.attachBuyButtonListeners();
			this.initVariantSelection();

			document.getElementById('remove-all-button')?.addEventListener('click', this.clearCart.bind(this));
			document.getElementById('checkout-button')?.addEventListener('click', this.checkout.bind(this));
		} catch (error) {
			// Error initializing cart
		}
	}

	initVariantSelection() {
		const colorButtons = document.querySelectorAll('.color-button');
		const sizeButtons = document.querySelectorAll('.size-button');
		const priceElement = document.querySelector('.price');
		// Support both .buy-button and .add-to-cart-button
		const buyButton = document.querySelector('.buy-button') || document.querySelector('.add-to-cart-button');

		// Only require a buy button, price element is optional
		if (!buyButton) {
			return;
		}

		let selectedColor = null;
		let selectedSize = null;

		const updateVariant = () => {
			const variantsElement = document.getElementById('variants-json');

			if (!variantsElement) {
				if (priceElement) priceElement.textContent = 'Pris ikke tilgjengelig';
				buyButton.dataset.defaultVariantId = '';
				return;
			}

			try {
				const variants = JSON.parse(variantsElement.textContent || '[]');
				if (!Array.isArray(variants) || variants.length === 0) {
					if (priceElement) priceElement.textContent = 'Pris ikke tilgjengelig';
					buyButton.dataset.defaultVariantId = '';
					return;
				}

				let matchingVariant = null;

				// Helper function to find option by flexible name matching
				const findOption = (selectedOptions, optionType) => {
					if (!selectedOptions) return null;

					const colorNames = ['color', 'farge', 'colour', 'couleur'];
					const sizeNames = ['size', 'størrelse', 'taille', 'größe'];

					if (optionType === 'color') {
						return selectedOptions.find(opt =>
							colorNames.includes(opt.name.toLowerCase())
						) || selectedOptions[0]; // Fallback to first option
					} else if (optionType === 'size') {
						return selectedOptions.find(opt =>
							sizeNames.includes(opt.name.toLowerCase())
						) || selectedOptions[1]; // Fallback to second option
					}
					return null;
				};

				// If we have color and size buttons, try to find matching variant
				if (colorButtons.length > 0 && sizeButtons.length > 0) {
					matchingVariant = variants.find((variant) => {
						const colorOption = findOption(variant.selectedOptions, 'color');
						const sizeOption = findOption(variant.selectedOptions, 'size');
						return colorOption?.value === selectedColor && sizeOption?.value === selectedSize;
					});
				}
				// If only color buttons exist
				else if (colorButtons.length > 0) {
					matchingVariant = variants.find((variant) => {
						const colorOption = findOption(variant.selectedOptions, 'color');
						return colorOption?.value === selectedColor;
					});
				}
				// If only size buttons exist
				else if (sizeButtons.length > 0) {
					matchingVariant = variants.find((variant) => {
						const sizeOption = findOption(variant.selectedOptions, 'size');
						return sizeOption?.value === selectedSize;
					});
				}
				// If no variant buttons, use default
				else {
					matchingVariant = variants[0];
				}

				// Fallback to first variant if no match found
				if (!matchingVariant) {
					matchingVariant = variants[0];
				}

				if (matchingVariant && matchingVariant.price) {
					if (priceElement) priceElement.textContent = `${parseFloat(matchingVariant.price).toFixed(0)} NOK`;
					buyButton.dataset.defaultVariantId = matchingVariant.id;
				} else {
					if (priceElement) priceElement.textContent = 'Pris er ikke tilgjengelig';
					buyButton.dataset.defaultVariantId = '';
				}
			} catch (error) {
				if (priceElement) priceElement.textContent = 'Pris ikke tilgjengelig';
				buyButton.dataset.defaultVariantId = '';
			}
		};

		colorButtons.forEach(button => {
			button.addEventListener('click', () => {
				selectedColor = button.dataset.color;

				colorButtons.forEach(btn => btn.classList.remove('selected'));
				button.classList.add('selected');

				updateVariant();
			});
		});

		sizeButtons.forEach(button => {
			button.addEventListener('click', () => {
				selectedSize = button.dataset.size;

				// Highlight the selected button
				sizeButtons.forEach(btn => btn.classList.remove('selected'));
				button.classList.add('selected');

				updateVariant();
			});
		});

		updateVariant();
	}

	updateVariantSelection() {
		const variants = JSON.parse(document.getElementById('variants-json').textContent);
		const colorSelect = document.getElementById('color-select');
		const sizeSelect = document.getElementById('size-select');
		const priceElement = document.querySelector('.price');
		const buyButton = document.querySelector('.buy-button');

		const selectedColor = colorSelect?.value;
		const selectedSize = sizeSelect?.value;

		// Helper function to find option by flexible name matching
		const findOption = (selectedOptions, optionType) => {
			if (!selectedOptions) return null;

			const colorNames = ['color', 'farge', 'colour', 'couleur'];
			const sizeNames = ['size', 'størrelse', 'taille', 'größe'];

			if (optionType === 'color') {
				return selectedOptions.find(opt =>
					colorNames.includes(opt.name.toLowerCase())
				) || selectedOptions[0]; // Fallback to first option
			} else if (optionType === 'size') {
				return selectedOptions.find(opt =>
					sizeNames.includes(opt.name.toLowerCase())
				) || selectedOptions[1]; // Fallback to second option
			}
			return null;
		};

		const matchingVariant = variants.find((variant) => {
			const colorOption = findOption(variant.selectedOptions, 'color');
			const sizeOption = findOption(variant.selectedOptions, 'size');
			return colorOption?.value === selectedColor && sizeOption?.value === selectedSize;
		});

		if (matchingVariant) {
			priceElement.textContent = `${matchingVariant.price} NOK`;
			buyButton.dataset.defaultVariantId = matchingVariant.id;
		} else {
			priceElement.textContent = 'Variant er ikke tilgjengelig';
			buyButton.dataset.defaultVariantId = '';
		}
	}

	async checkout() {
		try {
			const cart = await this.fetchCart(this.cartId);
			if (cart.checkoutUrl) {
				window.location.href = cart.checkoutUrl;
			}
		} catch (error) {
			// Error during checkout
		}
	}

	async createCart() {
		try {
			const query = `
				mutation {
					cartCreate {
						cart { id }
						userErrors { field message }
					}
				}`;

			const response = await fetch(`https://${process.env.SHOPIFY_HOSTNAME}/api/2023-07/graphql.json`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-Shopify-Storefront-Access-Token': process.env.SHOPIFY_STOREFRONT_ACCESS_TOKEN,
				},
				body: JSON.stringify({ query }),
			});

			if (!response.ok) {
				const errorText = await response.text();
				throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
			}

			const result = await response.json();

			if (!result.data || !result.data.cartCreate) {
				throw new Error('cartCreate is missing in response.');
			}

			const userErrors = result.data.cartCreate.userErrors || [];
			if (userErrors.length > 0) {
				throw new Error(userErrors.map((e) => e.message).join(', '));
			}

			const cartId = result.data.cartCreate.cart?.id;
			if (!cartId) {
				throw new Error('Cart ID is undefined.');
			}

			this.cartId = cartId;
			localStorage.setItem('cartId', cartId);
		} catch (error) {
			throw error; // Re-throw to be caught by init()
		}
	}

	async fetchCart(cartId) {
		try {
			const response = await fetch(`https://${process.env.SHOPIFY_HOSTNAME}/api/2023-07/graphql.json`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-Shopify-Storefront-Access-Token': process.env.SHOPIFY_STOREFRONT_ACCESS_TOKEN,
				},
				body: JSON.stringify({
					query: `
					query {
						cart(id: "${cartId}") {
							id
							checkoutUrl
							lines(first: 50) {
								edges {
									node {
										id
										quantity
										merchandise {
											... on ProductVariant {
												id
												title
												selectedOptions {
													name
													value
												}
												product {
													title
													images(first: 1) {
														edges {
															node {
																src
															}
														}
													}
												}
												price {
													amount
												}
											}
										}
									}
								}
							}
							cost {
								totalAmount {
									amount
								}
							}
						}
					}`,
				}),
			});

			const result = await response.json();

			if (!result.data || !result.data.cart) {
				throw new Error('Cart data is missing.');
			}

			return result.data.cart;
		} catch (error) {
			// Error fetching cart
		}
	}

	async addToCart(variantId, quantity = 1, options = {}) {
		try {
			if (!variantId) {
				throw new Error('Variant ID is required');
			}

			if (!this.cartId) {
				throw new Error('Cart ID is missing');
			}

			if (!process.env.SHOPIFY_HOSTNAME || !process.env.SHOPIFY_STOREFRONT_ACCESS_TOKEN) {
				throw new Error('Missing Shopify environment variables');
			}

			// Only add GID prefix if not already present
			const globalVariantId = variantId.startsWith('gid://')
				? variantId
				: `gid://shopify/ProductVariant/${variantId}`;

			const query = `
				mutation {
					cartLinesAdd(cartId: "${this.cartId}", lines: [{
						merchandiseId: "${globalVariantId}",
						quantity: ${quantity},
					}]) {
						cart {
							id
						}
						userErrors {
							field
							message
						}
					}
				}`;

			const response = await fetch(`https://${process.env.SHOPIFY_HOSTNAME}/api/2023-07/graphql.json`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-Shopify-Storefront-Access-Token': process.env.SHOPIFY_STOREFRONT_ACCESS_TOKEN,
				},
				body: JSON.stringify({ query }),
			});

			if (!response.ok) {
				const errorText = await response.text();
				throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
			}

			const result = await response.json();

			if (!result.data || !result.data.cartLinesAdd) {
				throw new Error('cartLinesAdd failed.');
			}

			const userErrors = result.data.cartLinesAdd.userErrors || [];
			if (userErrors.length > 0) {
				throw new Error(userErrors.map((e) => e.message).join(', '));
			}

			const updatedCart = await this.fetchCart(this.cartId);

			if (updatedCart) {
				this.updateCartDisplay(updatedCart);
				this.showSidebarCart();
			} else {
				throw new Error('Failed to fetch updated cart');
			}
		} catch (error) {
			this.showToast('Kunne ikke legge til produkt i handlekurven. Vennligst prøv igjen.', 'error');
		}
	}

	async removeItemFromCart(lineItemId) {
		try {
			const response = await fetch(`https://${process.env.SHOPIFY_HOSTNAME}/api/2023-07/graphql.json`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-Shopify-Storefront-Access-Token': process.env.SHOPIFY_STOREFRONT_ACCESS_TOKEN,
				},
				body: JSON.stringify({
					query: `
					mutation {
						cartLinesRemove(cartId: "${this.cartId}", lineIds: ["${lineItemId}"]) {
							cart {
								id
							}
							userErrors {
								field
								message
							}
						}
					}`,
				}),
			});

			const result = await response.json();

			if (!result.data || !result.data.cartLinesRemove) {
				throw new Error('cartLinesRemove failed.');
			}

			const userErrors = result.data.cartLinesRemove.userErrors || [];
			if (userErrors.length > 0) {
				throw new Error(userErrors.map((e) => e.message).join(', '));
			}

			const updatedCart = await this.fetchCart(this.cartId);

			this.updateCartDisplay(updatedCart);
		} catch (error) {
			// Error removing item
		}
	}

	async clearCart() {
		try {
			const cart = await this.fetchCart(this.cartId);
			if (!cart || !cart.lines.edges.length) return;

			const lineIds = cart.lines.edges.map((edge) => edge.node.id);

			const response = await fetch(`https://${process.env.SHOPIFY_HOSTNAME}/api/2023-07/graphql.json`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-Shopify-Storefront-Access-Token': process.env.SHOPIFY_STOREFRONT_ACCESS_TOKEN,
				},
				body: JSON.stringify({
					query: `
					mutation {
					  cartLinesRemove(cartId: "${this.cartId}", lineIds: [${lineIds.map(id => `"${id}"`).join(', ')}]) {
						cart { id }
						userErrors {
							field
							message
						}
					  }
					}
				  `,
				}),
			});

			const result = await response.json();

			if (!result.data || !result.data.cartLinesRemove) {
				throw new Error('cartLinesRemove failed.');
			}

			const userErrors = result.data.cartLinesRemove.userErrors || [];
			if (userErrors.length > 0) {
				throw new Error(userErrors.map((e) => e.message).join(', '));
			}

			this.updateCartDisplay({ lines: { edges: [] }, cost: { totalAmount: { amount: 0 } } });
		} catch (error) {
			// Error clearing cart
		}
	}

	updateCartDisplay(cart) {
		const cartItemsContainer = document.getElementById('cart-items');
		const cartTotal = document.getElementById('cart-total');
		const removeAllButton = document.getElementById('remove-all-button');
		const checkoutButton = document.getElementById('checkout-button');
		const cartBottom = document.getElementById('cart-bottom');

		cartItemsContainer.innerHTML = '';

		if (!cart.lines.edges.length) {
			cartItemsContainer.innerHTML = '<li class="text-sm">Handlekurven er tom.</li>';
			cartTotal.textContent = '0 NOK';
			removeAllButton.style.display = 'none';
			checkoutButton.style.display = 'none';
			cartBottom.style.display = 'none';
			return;
		}

		cart.lines.edges.forEach(({ node }) => {
			const { merchandise, quantity } = node;

			const productTitle = merchandise.product?.title;
			const price = merchandise.price?.amount ? `${parseFloat(merchandise.price.amount).toFixed(0)} NOK` : '';
			const imageSrc = merchandise.product?.images?.edges?.[0]?.node?.src || null;
			const refreshedImageSrc = imageSrc;
			const variantOptions = merchandise.selectedOptions.map(option => `${option.value}`).join(' – ');

			const cartItem = document.createElement('li');
			cartItem.classList.add('cart-item');

			cartItem.innerHTML = `
				<div class="flex gap-20">
					<figure class="w-2/6 md:w-1/4">
						<img src="${refreshedImageSrc}" alt="${productTitle}" class="cart-item-image rounded-lg" />
					</figure>
					<div class="cart-item-details flex flex-col justify-center">
						<div class="font-medium text-sm md:text-base mb-12">${productTitle}</div>
						<div class="text-xs md:text-sm opacity-60 mb-12">${variantOptions}</div>
						<div class="text-xs md:text-sm mb-12">${price}</div>
						<button class="remove-item font-medium text-xs md:text-sm border-b border-b-white/60 w-fit text-white group-hover:text-white/60 hover:border-b-white/0 transition-all duration-500 ease-bounce" data-line-id="${node.id}">Fjern</button>
					</div>
				</div>
			`;

			cartItemsContainer.appendChild(cartItem);

			cartItem.querySelector('.remove-item').addEventListener('click', () => {
				this.removeItemFromCart(node.id);
			});
		});

		const totalAmount = cart.cost?.totalAmount?.amount ? parseFloat(cart.cost.totalAmount.amount).toFixed(0) : '0';
		cartTotal.textContent = `${totalAmount} NOK`;

		removeAllButton.style.display = 'block';
		checkoutButton.style.display = 'block';
		cartBottom.style.display = 'block';
	}

	attachBuyButtonListeners() {
		const buyButtons = document.querySelectorAll('.buy-button, .add-to-cart-button');
		buyButtons.forEach((button) => {
			button.addEventListener('click', (event) => {
				event.preventDefault();

				// Retrieve selected options from highlighted buttons
				const selectedColorButton = document.querySelector('.color-button.selected');
				const selectedSizeButton = document.querySelector('.size-button.selected');

				const selectedColor = selectedColorButton?.dataset.color;
				const selectedSize = selectedSizeButton?.dataset.size;

				// Check if variant selection is required based on available options
				const colorButtons = document.querySelectorAll('.color-button');
				const sizeButtons = document.querySelectorAll('.size-button');
				const hasColorOptions = colorButtons.length > 0;
				const hasSizeOptions = sizeButtons.length > 0;

				// Only require selection for options that exist on the page
				const needsColor = hasColorOptions && !selectedColor;
				const needsSize = hasSizeOptions && !selectedSize;

				if (needsColor || needsSize) {
					let message = 'Vennligst velg ';
					if (needsColor && needsSize) {
						message += 'farge og størrelse';
					} else if (needsColor) {
						message += 'farge';
					} else {
						message += 'størrelse';
					}
					message += ' før du legger til i handlekurven.';
					this.showToast(message, 'error');
					return;
				}

				// Pass selected options to the addToCart method
				const variantId = button.dataset.defaultVariantId;
				if (!variantId) {
					this.showToast('Produktvariant er ikke tilgjengelig.', 'error');
					return;
				}

				this.addToCart(variantId, 1, { color: selectedColor, size: selectedSize });
			});
		});
	}


	showSidebarCart() {
		const sidebarCart = document.getElementById('cart');
		const body = document.querySelector('body');
		const overlay = document.getElementById('overlay');

		if (!sidebarCart) {
			return;
		}

		sidebarCart.classList.add('!right-0', '!opacity-100', '!visible');
		body.classList.add('overflow-hidden');

		if (overlay) {
			overlay.classList.add('!visible', '!opacity-25');
		}

		document.addEventListener('click', this.handleOutsideClick);
	}

	closeSidebarCart() {
		const sidebarCart = document.getElementById('cart');
		const body = document.querySelector('body');
		const overlay = document.getElementById('overlay');

		if (!sidebarCart) {
			return;
		}

		sidebarCart.classList.remove('!right-0', '!opacity-100', '!visible');
		body.classList.remove('overflow-hidden');

		if (overlay) {
			overlay.classList.remove('!visible', '!opacity-25');
		}

		document.removeEventListener('click', this.handleOutsideClick);
	}

	handleOutsideClick(event) {
		const sidebarCart = document.getElementById('cart');
		const cartIcon = document.getElementById('cart-icon');

		if (!sidebarCart) return;

		// Don't close if clicking on cart icon or inside cart
		if (cartIcon && cartIcon.contains(event.target)) {
			return;
		}

		if (!sidebarCart.contains(event.target)) {
			this.closeSidebarCart();
		}
	}

	attachCartIconListener() {
		document.getElementById('cart-icon')?.addEventListener('click', (e) => {
			e.stopPropagation();
			this.showSidebarCart();
		});
	}

	attachCloseCartListener() {
		document.getElementById('cart-close')?.addEventListener('click', this.closeSidebarCart);
	}

	bindRemoveButtons() {
		document.querySelectorAll('.remove-item').forEach((btn) => {
			btn.addEventListener('click', () => {
				this.removeItemFromCart(btn.dataset.lineId);
			});
		});
	}
}
